import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { useActiveState, useClickOutsideComponent } from '@hcs/hooks';
import { DateStr, InputProps, TDateISONoSec } from '@hcs/types';
import { formatDateLong, formatDateTime } from '@hcs/utils';

import { CloseIcon, OmCalendarIcon } from '../../../../svgs';
import { IconButton } from '../../buttons';
import { Input } from '../Input';

import styles from './DateInput.module.css';

type ParentRef = HTMLInputElement | null;

export interface DateInputProps
  extends Omit<InputProps, 'value' | 'onChange' | 'theme'> {
  dataHcName: string;
  className?: string;
  name?: string;
  value: string | null;
  theme?: {
    InactiveDisplay?: string;
  };
  onChange?: (val: string | null) => void;
  onSetDate?: (val: DateStr | TDateISONoSec | null) => void;
  onCancel?: VoidFunction;
  onBlur?: () => void;
}
export const DateInput = React.forwardRef<ParentRef, DateInputProps>(
  (
    {
      dataHcName,
      className,
      value,
      theme,
      onChange,
      onSetDate,
      onCancel,
      onBlur,
      error,
      placeholder,
      ...inputProps
    }: DateInputProps,
    ref,
  ) => {
    const inputContainerRef = useRef<HTMLDivElement>(null);
    const [innerValue, setInnerValue] = useState(value || '');
    const { active, handleClose, handleOpen } = useActiveState();
    useEffect(() => {
      setInnerValue(value || '');
    }, [value]);
    const handleCancel = () => {
      setInnerValue(value || '');
      onCancel?.();
      handleClose();
      if (onBlur) {
        onBlur();
      }
    };
    const handleSetDate = () => {
      if (active) {
        if (value === innerValue || (!value && !innerValue)) {
          handleCancel();
        } else {
          onSetDate?.(
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            innerValue === '' ? null : (innerValue as DateStr | TDateISONoSec), // It will either be an empty string or DateStr
          );
          handleClose();
        }
        if (onBlur) {
          onBlur();
        }
      }
    };
    useClickOutsideComponent(inputContainerRef, handleSetDate);
    return (
      <div className={className}>
        <div
          className={classNames(styles.DateInput, {
            [styles.inactive]: !active,
          })}
        >
          {active ? (
            <div className={styles.ActiveDisplay} ref={inputContainerRef}>
              <Input
                {...inputProps}
                dataHcName={`${dataHcName}-valid-till`}
                type={inputProps?.type || 'date'}
                className={styles.Input}
                value={innerValue || ''}
                onChange={(v) => {
                  setInnerValue(v);
                  onChange?.(v || null);
                }}
                theme={{
                  InputElement: styles.InputElement,
                }}
                ref={ref}
              />
              <IconButton
                dataHcName="cancel"
                onClick={handleCancel}
                icon={<CloseIcon />}
              />
            </div>
          ) : (
            <div
              className={classNames(
                styles.InactiveDisplay,
                theme?.InactiveDisplay,
              )}
              onClick={handleOpen}
            >
              <OmCalendarIcon size="sm" />
              {value ? (
                inputProps?.type === 'datetime-local' ? (
                  formatDateTime(value)
                ) : (
                  formatDateLong(value)
                )
              ) : !active ? (
                <div className={styles.AddDate}>
                  {placeholder || 'Add Date'}
                </div>
              ) : null}
            </div>
          )}
        </div>
        {error ? (
          <div data-hc-name={`${dataHcName}-error`} className={styles.Error}>
            {error}
          </div>
        ) : null}
      </div>
    );
  },
);
